import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Spinner, SpinnerSize } from '@fluentui/react'
import { useTranslation } from 'react-i18next'
import { TreeItemLayout } from '@fluentui/react-components/unstable'
import useAxios from '../../hooks/useAxios'
import { Destination, IGraphArrayResponse } from '../../context/AxiosContext'
import { driveItemListProperties, driveItemOverviewProperties } from '../../helpers/documentHelper'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { Axios } from 'axios'
import {
    Badge,
    FlatTree,
    FlatTreeItem,
    FlatTreeItemProps,
    FlatTreeProps,
    HeadlessFlatTreeItemProps,
    TreeItemValue,
    TreeSelectionValue,
    useHeadlessFlatTree_unstable,
} from '@fluentui/react-components'
import { bundleIcon, DocumentFilled, DocumentRegular, FolderOpenFilled, FolderRegular } from '@fluentui/react-icons'
import { HeadlessFlatTree, TreeCheckedChangeData } from '@fluentui/react-tree'
import FormattedDateTime from '../Date/FormattedDateTime'

const DocumentIcon = bundleIcon(DocumentFilled, DocumentRegular)

type DocumentFlatTreeProps = FlatTreeProps & {
    rootId: string
    checkedValues?: string[]
    setCheckedValues?: (values: string[]) => void
}

type DocumentFlatTreeItemProps = FlatTreeItemProps & {
    item: FolderDriveItem
    flatTreeHook: HeadlessFlatTree<FolderDriveItem>
}

type FolderDriveItem = HeadlessFlatTreeItemProps &
    DriveItem & {
        parent: FolderDriveItem
        children: FolderDriveItem[]
        childCount: number
    }

const mapDriveItem = (item: DriveItem, parentItem: FolderDriveItem, rootId: string): FolderDriveItem => {
    return {
        ...item,
        value: item.id,
        parentValue: item.parentReference.id === rootId ? undefined : item.parentReference.id,
        itemType: item.folder ? 'branch' : 'leaf',
        parent: parentItem,
        children: [],
        childCount: item.folder?.childCount || 0,
    }
}

const fetchDriveItemChildren = (axios: Axios, id: string): Promise<IGraphArrayResponse<DriveItem>> => {
    return axios.get(`/items/${id}/children`, {
        baseURL: Destination.Documents,
        params: { $select: driveItemOverviewProperties.join(',') },
        headers: { SuppressErrors: 'true' },
    }) as Promise<IGraphArrayResponse<DriveItem>>
}

const mapResponse = (axios: Axios, driveItem: DriveItem, parentItem: FolderDriveItem, rootId: string): Promise<FolderDriveItem[]> => {
    return Promise.resolve(mapDriveItem(driveItem, parentItem, rootId)).then(folderDriveItem => {
        if (folderDriveItem.childCount > 0) {
            return fetchDriveItemChildren(axios, folderDriveItem.id)
                .then(children => {
                    return children.data?.value?.flatMap(child => mapResponse(axios, child, folderDriveItem, rootId))
                })
                .then(result => Promise.all(result))
                .then(children => children.reduce((a, b) => a.concat(b)))
                .then(children => {
                    if (parentItem === undefined) {
                        return [...children]
                    } else {
                        return [{ ...folderDriveItem, children: children }, ...children]
                    }
                })
        } else {
            if (parentItem === undefined) {
                return Promise.resolve([])
            } else {
                return Promise.resolve([folderDriveItem])
            }
        }
    })
}

function DocumentFlatTreeItem(props: DocumentFlatTreeItemProps): ReactElement {
    const isFolder = useMemo(() => props.itemType === 'branch', [props.item])
    const isFile = useMemo(() => props.itemType === 'leaf', [props.item])
    const isOpen = useMemo(() => {
        return [...props.flatTreeHook.getTreeProps().openItems].some(id => id === props.value)
    }, [props.item, props.flatTreeHook.getTreeProps().openItems])

    return (
        <FlatTreeItem {...props}>
            <TreeItemLayout
                iconBefore={isFolder ? isOpen ? <FolderOpenFilled /> : <FolderRegular /> : <DocumentIcon />}
                iconAfter={
                    isFolder && (
                        <Badge size={'small'} appearance={'tint'}>
                            {props.item.childCount}
                        </Badge>
                    )
                }
                aside={isFile && <FormattedDateTime date={props.item.lastModifiedDateTime} />}
                // actions={
                //     <Menu>
                //         <MenuTrigger disableButtonEnhancement>
                //             <Button aria-label="More options" appearance="subtle" icon={<MoreHorizontalRegular />} />
                //         </MenuTrigger>
                //         <MenuPopover>
                //             <MenuList>
                //                 <MenuItem>New </MenuItem>
                //                 <MenuItem>New Window</MenuItem>
                //             </MenuList>
                //         </MenuPopover>
                //     </Menu>
                // }
            >
                {props.item.name}
            </TreeItemLayout>
        </FlatTreeItem>
    )
}

export default function DocumentFlatTree(props: DocumentFlatTreeProps): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const [items, setItems] = useState<FolderDriveItem[]>()
    const [checkedItems, setCheckedItems] = useState<Iterable<[TreeItemValue, TreeSelectionValue]>>([])

    const flatTree = useHeadlessFlatTree_unstable(items, {
        selectionMode: props.checkedValues ? 'multiselect' : undefined,
        onCheckedChange: (_, data: TreeCheckedChangeData) => {
            setCheckedItems(data.checkedItems)
            props.setCheckedValues(
                [...data.checkedItems]
                    .filter(
                        ([checkedItem, checkedValue]) => items.find(item => item.value === checkedItem)?.itemType === 'leaf' && checkedValue === true,
                    )
                    .map(([item]) => item as string),
            )
        },
        checkedItems: checkedItems,
    })

    useEffect(() => {
        if (props.rootId) {
            axios
                .get<DriveItem>(`/items/${props.rootId}`, {
                    baseURL: Destination.Documents,
                    params: { $select: driveItemListProperties.join(','), $expand: 'children' },
                    headers: { SuppressErrors: 'true' },
                })
                .then(response => {
                    console.log(
                        'folder count is ',
                        response.data.children?.map(child => child.folder?.childCount || 0).reduce((a, b) => a + b, 0) +
                            response.data.folder?.childCount,
                    )
                    return mapResponse(axios, response.data, undefined, response.data.id)
                })
                .then(result => {
                    setItems(result)

                    setCheckedItems(
                        props.checkedValues
                            ?.map(value => result.find(item => item.value === value))
                            .filter(Boolean)
                            .flatMap(value => {
                                const hierarchy: [TreeItemValue, TreeSelectionValue][] = []
                                hierarchy.push([value.id, true])
                                let parentItem = result.find(item => item.value === value.parentValue)
                                while (parentItem !== undefined) {
                                    hierarchy.push([parentItem.id, 'mixed'])
                                    parentItem = result.find(item => item.value === parentItem.parentValue)
                                }
                                return hierarchy
                            }) || [],
                    )
                })
        } else {
            setItems(undefined)
        }
    }, [props.rootId])

    if (!items) {
        return <Spinner label={translate('base.loading')} size={SpinnerSize.xSmall} labelPosition="right" />
    }

    return (
        <FlatTree {...props} {...flatTree.getTreeProps()} aria-label={items?.[0]?.name}>
            {Array.from(flatTree.items(), flatTreeItem => {
                return (
                    <DocumentFlatTreeItem
                        key={flatTreeItem.value}
                        {...flatTreeItem.getTreeItemProps()}
                        item={flatTreeItem.getTreeItemProps()}
                        flatTreeHook={flatTree}
                    />
                )
            })}
        </FlatTree>
    )
}
